import {
  ReactElement,
  Suspense,
  createContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { LinearProgress, ThemeProvider } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import themeConfig from "./assets/styles/theme";
import "./assets/styles/App.scss";
import IndexPage from "pages";
import Layout from "components/layout";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useRoutes,
} from "react-router-dom";
import ApplyLoan from "pages/apply-loan";
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "state/RootReducer";
import { ProgressProvider, useProgress } from "context/ProgressProvider";
// --------- SBL
import {
  AgreementsSigningSBL,
  BankDetailsSBL,
  DirectorsSBL,
  IdentityVerificationSBL,
  LiabilitiesLegalTiesSBL,
  PropertyDetailsSBL,
  ReviewDetailsSBL,
  SolicitorDetailsSBL,
  YourBusinessSBL,
  YourDetailsSBL,
  YourLoanSBL,
} from "components/apply-loan/sbl";
// --------- RND
import {
  AccountantDetailsRND,
  AgreementsSigningRND,
  BankDetailsRND,
  BusinessDetailsRND,
  ConditionalApprovalRND,
  DirectorsRND,
  IdentityVerificationRND,
  LoanDetailsRND,
  YourDetailsRND,
} from "components/apply-loan/rnd/";

import LandingPage from "pages/landing";
import ContactUs from "pages/contact-us/ContactUs";
import NotFoundPage from "pages/NotFoundPage";
import AboutUs from "pages/about-us/AboutUs";
import PrivacyPolicy from "pages/privacy-policy/PrivacyPolicy";
import TermOfService from "pages/terms-of-service/TermOfService";
import DialogIdle from "components/DialogIdle";
import ThemesProvider from "context/ThemesProvider";
import ThankyouPage from "pages/ThankyouPage";
import { ProfileActions } from "state/profile/Action";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Brokers from "pages/brokers";
import { useAppIdleTimer } from "shared/helpers/Utils";

export interface AppContextProps {
  landingRoute: string;
  setLandingRoute: (route: string) => void;
}

export const AppContext = createContext<AppContextProps | null>(null);

const AppRoutes = () => {
  const authInfo = useSelector((state: any) => state.authState);
  const location = useLocation();
  const { currentSectionId } = useProgress();
  const lastToastRoute = useRef<string | null>(null);
  const dispatch = useDispatch();

  const routes = useRoutes([
    { path: "/", element: <IndexPage /> },
    {
      path: "/sbl",
      children: [
        { index: true, element: <IndexPage path="sbl" /> },
        {
          path: "",
          element: <ApplyLoan />,
          children: [
            // Introduction
            {
              path: "intro-details",
              element: <YourDetailsSBL />,
              children: [{ path: ":step", element: <YourDetailsSBL /> }],
            },
            {
              path: "intro-loan",
              element: <YourLoanSBL />,
              children: [{ path: ":step", element: <YourLoanSBL /> }],
            },
            {
              path: "intro-business",
              element: <YourBusinessSBL />,
              children: [{ path: ":step", element: <YourBusinessSBL /> }],
            },

            // Security
            {
              path: "sec-property",
              element: <PropertyDetailsSBL />,
              children: [{ path: ":step", element: <PropertyDetailsSBL /> }],
            },
            {
              path: "sec-liabilities",
              element: <LiabilitiesLegalTiesSBL />,
            },

            // Verification
            {
              path: "verif-review",
              element: <ReviewDetailsSBL />,
              children: [{ path: ":step", element: <PropertyDetailsSBL /> }],
            },
            { path: "verif-directors", element: <DirectorsSBL /> },
            { path: "verif-identity", element: <IdentityVerificationSBL /> },

            // Finalise
            { path: "final-solicitor", element: <SolicitorDetailsSBL /> },
            { path: "final-bank", element: <BankDetailsSBL /> },
            {
              path: "final-agreements",
              element: <AgreementsSigningSBL />,
              children: [{ path: ":step", element: <AgreementsSigningSBL /> }],
            },
          ],
        },
      ],
    },
    {
      path: "/rnd",
      children: [
        { index: true, element: <IndexPage path="rnd" /> },
        {
          path: "",
          element: <ApplyLoan />,
          children: [
            // Introduction
            {
              path: "intro-details",
              element: <YourDetailsRND />,
              children: [{ path: ":step", element: <YourDetailsRND /> }],
            },
            {
              path: "intro-loan",
              element: <LoanDetailsRND />,
              children: [{ path: ":step", element: <LoanDetailsRND /> }],
            },

            //Review Details
            {
              path: "review-approval",
              element: <ConditionalApprovalRND />,
              children: [
                { path: ":step", element: <ConditionalApprovalRND /> },
              ],
            },

            //Finalise
            {
              path: "final-business",
              element: <BusinessDetailsRND />,
              children: [{ path: ":step", element: <BusinessDetailsRND /> }],
            },
            {
              path: "final-directors",
              element: <DirectorsRND />,
              children: [{ path: ":step", element: <DirectorsRND /> }],
            },
            {
              path: "final-identity",
              element: <IdentityVerificationRND />,
              children: [
                { path: ":step", element: <IdentityVerificationRND /> },
              ],
            },
            {
              path: "final-accountant",
              element: <AccountantDetailsRND />,
              children: [{ path: ":step", element: <AccountantDetailsRND /> }],
            },
            {
              path: "final-bank",
              element: <BankDetailsRND />,
              children: [{ path: ":step", element: <BankDetailsRND /> }],
            },
            {
              path: "final-agreements",
              element: <AgreementsSigningRND />,
              children: [{ path: ":step", element: <AgreementsSigningRND /> }],
            },
          ],
        },
      ],
    },
    { path: "/broker", element: <Brokers /> },
    { path: "/privacy-policy", element: <PrivacyPolicy /> },
    { path: "/terms-of-service", element: <TermOfService /> },
    { path: "/thank-you", element: <ThankyouPage /> },
    { path: "*", element: <NotFoundPage /> },
  ]);

  //No Token
  const isSblApplyRoute = /^\/sbl(\/|$)/.test(location.pathname);
  const isRndApplyRoute = /^\/rnd(\/|$)/.test(location.pathname);
  const basePath = isSblApplyRoute ? "/sbl" : "/rnd";
  const introPaths = [`${basePath}`, `${basePath}/`];

  const isAllowedIntro =
    introPaths.includes(location.pathname) ||
    location.pathname.startsWith(`${basePath}/intro-details`);
  if (isSblApplyRoute && !authInfo.isUserLoggedIn && !isAllowedIntro) {
    return <Navigate to={`${basePath}/intro-details`} replace />;
  }
  if (isRndApplyRoute && !authInfo.isUserLoggedIn && !isAllowedIntro) {
    return <Navigate to={`${basePath}/intro-details`} replace />;
  }

  //-----
  const privateRoutes = ["/sbl", "/rnd"];
  if (privateRoutes.some((route) => location.pathname.startsWith(route))) {
    return <Layout>{routes}</Layout>;
  }
  return <>{routes}</>;
};

const LandingPageRoutes = () => {
  const location = useLocation();

  const routes = useRoutes([
    { path: "/", element: <LandingPage /> },
    { path: "/contact-us", element: <ContactUs /> },
    { path: "/about-us", element: <AboutUs /> },
    { path: "/privacy-policy", element: <PrivacyPolicy /> },
    { path: "/terms-of-service", element: <TermOfService /> },
    { path: "*", element: <NotFoundPage /> },
  ]);

  return <>{routes}</>;
};

const App = (): ReactElement => {
  const hostName = window.location.hostname;
  const landingRoute = process.env.REACT_APP_LANDING_ROUTE;
  const isLandingPage = hostName === landingRoute;
  const [idleOpen, setIdleOpen] = useState(false);

  const handleIdle = () => {
    setIdleOpen(true);
  };

  useAppIdleTimer(handleIdle, 3600000);

  return (
    <Provider store={store}>
      <Suspense fallback={<LinearProgress color="primary" />}>
        <BrowserRouter>
          <ProgressProvider>
            <ThemesProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DialogIdle idleOpen={idleOpen} setIdleOpen={setIdleOpen} />
                {isLandingPage ? <LandingPageRoutes /> : <AppRoutes />}
              </LocalizationProvider>
            </ThemesProvider>
          </ProgressProvider>
        </BrowserRouter>
      </Suspense>
      <ToastContainer />
    </Provider>
  );
};

export default App;
